import { useEffect, useRef, useState } from "react"

import { AssessmentTab } from "@/admin/monitoring/assessment-tab"
import {
  CompanyRunHeader,
  CompanyRunSpend,
} from "@/admin/monitoring/company-run-header"
import {
  type CompanyRunResearch,
  loadCompanyRunResearch,
  loadResearchTaskReceipts,
  type ResearchTaskReceipts,
} from "@/admin/monitoring/company-run-research-data"
import { CompanyRunResearchView } from "@/admin/monitoring/company-run-research-view"
import { CompanyRunStageStrip } from "@/admin/monitoring/company-run-stages"
import { AssessmentStatus } from "@/admin/monitoring/compilation-status"
import {
  type FileEvidenceTarget,
  loadFileEvidence,
} from "@/admin/monitoring/file-evidence-data"
import { FileView } from "@/admin/monitoring/file-view"
import {
  type OpenedFileEvidence,
  RunEvidenceSheet,
} from "@/admin/monitoring/run-evidence-sheet"
import {
  liveRefreshMs,
  useVisibleInterval,
} from "@/admin/monitoring/use-visible-interval"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type { ResearchCategory } from "@/constants/terminal/research-category"
import { errorMessage } from "@/lib/error-message"

export const companyRunTabs = ["research", "assessment", "file"] as const
type CompanyRunTab = (typeof companyRunTabs)[number]

export const CompanyRunPage = ({
  research: loadedResearch,
  tab,
  category,
  onSelectCategory,
  researchTaskId,
  onSelectTab,
  onOpenResearchTask,
  onSelectRun,
}: {
  research: CompanyRunResearch
  category?: ResearchCategory
  onSelectCategory: (category: ResearchCategory) => void
  tab: CompanyRunTab
  researchTaskId: string | null
  onSelectTab: (tab: CompanyRunTab) => void
  onOpenResearchTask: (id: string | null) => void
  onSelectRun: (id: string) => void
}) => {
  const [research, setResearch] = useState(loadedResearch)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  useEffect(() => {
    setResearch(loadedResearch)
    setRefreshedAt(new Date())
  }, [loadedResearch])
  // An unfinished Company Run refreshes itself while someone is looking at
  // it; selected category and Research Task live in the URL, so they hold.
  const isUnfinished = research.outcome === null
  const isRefreshPending = useRef(false)
  useVisibleInterval(() => {
    if (isRefreshPending.current) return
    isRefreshPending.current = true
    void loadCompanyRunResearch({
      data: { companyRunId: research.companyRunId },
    })
      .then((next) => {
        setResearch(next)
        setRefreshedAt(new Date())
        setRefreshError(null)
      })
      .catch((cause: unknown) =>
        setRefreshError(errorMessage(cause, "Refresh failed"))
      )
      .finally(() => {
        isRefreshPending.current = false
      })
  }, isUnfinished)
  const evidenceFocus = useRef<HTMLElement | null>(null)
  const taskFocusId = useRef(researchTaskId)
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false)
  const [openedEvidence, setOpenedEvidence] =
    useState<OpenedFileEvidence | null>(null)
  const [evidence, setEvidence] = useState<Awaited<
    ReturnType<typeof loadFileEvidence>
  > | null>(null)
  const [receipts, setReceipts] = useState<ResearchTaskReceipts | null>(null)
  const [evidenceError, setEvidenceError] = useState<string | null>(null)
  const [hasModelPayloads, setHasModelPayloads] = useState(false)
  useEffect(() => {
    let isCancelled = false
    if (!hasModelPayloads) {
      setEvidence(null)
      setReceipts(null)
    }
    setEvidenceError(null)
    setIsLoadingEvidence(researchTaskId !== null || openedEvidence !== null)
    if (researchTaskId) {
      void loadResearchTaskReceipts({
        data: {
          companyRunId: research.companyRunId,
          researchTaskId,
          hasModelPayloads,
        },
      })
        .then((loaded) => {
          if (!isCancelled) {
            setReceipts(loaded)
            setIsLoadingEvidence(false)
          }
        })
        .catch((cause: unknown) => {
          if (!isCancelled) {
            setIsLoadingEvidence(false)
            setEvidenceError(errorMessage(cause, "Evidence failed"))
          }
        })
    } else if (openedEvidence) {
      void loadFileEvidence({
        data: {
          companyRunId: research.companyRunId,
          target: openedEvidence.target,
          hasModelPayloads,
        },
      })
        .then((loaded) => {
          if (!isCancelled) {
            setEvidence(loaded)
            setIsLoadingEvidence(false)
          }
        })
        .catch((cause: unknown) => {
          if (!isCancelled) {
            setIsLoadingEvidence(false)
            setEvidenceError(errorMessage(cause, "Evidence failed"))
          }
        })
    }
    return () => {
      isCancelled = true
    }
  }, [research.companyRunId, researchTaskId, openedEvidence, hasModelPayloads])
  // Only a completed Assessment's parts reach the buyer.
  const liveAssessment = research.parts.isLive
    ? research.parts.assessment
    : null
  const openEvidence = (target: FileEvidenceTarget, title: string) => {
    evidenceFocus.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    taskFocusId.current = null
    onOpenResearchTask(null)
    setHasModelPayloads(false)
    setOpenedEvidence({ target, title })
  }
  const openTask = (id: string) => {
    taskFocusId.current = id
    evidenceFocus.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    setHasModelPayloads(false)
    setOpenedEvidence(null)
    onOpenResearchTask(id)
  }
  return (
    <div className="space-y-10 pb-16">
      <CompanyRunHeader research={research} onSelectRun={onSelectRun} />
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <CompanyRunStageStrip run={research} />
        {isUnfinished && (
          <p className="text-muted-foreground flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-2">
              <span className="bg-terminal-accent size-2 rounded-full motion-safe:animate-pulse" />
              Live, every {liveRefreshMs / 1000} seconds
            </span>
            {refreshedAt && (
              <span className="tabular-nums">
                Refreshed {refreshedAt.toLocaleTimeString()}
              </span>
            )}
          </p>
        )}
      </div>
      {refreshError && (
        <p
          role="alert"
          className="border-assessment-critical text-assessment-critical rounded-sm border p-3 text-sm"
        >
          Refresh failed: {refreshError}. Showing the last successful view.
        </p>
      )}
      <div className="space-y-8">
        <Tabs
          value={tab}
          onValueChange={(value) =>
            onSelectTab(
              companyRunTabs.find((name) => name === value) ?? "research"
            )
          }
        >
          <TabsList
            variant="line"
            className="h-10 w-full justify-start border-b"
          >
            <TabsTrigger value="research" className="flex-none px-3">
              Research
            </TabsTrigger>
            <TabsTrigger value="assessment" className="flex-none px-3">
              Assessment
            </TabsTrigger>
            <TabsTrigger value="file" className="flex-none px-3">
              File
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "research" && (
          <CompanyRunResearchView
            research={research}
            category={category}
            onSelectCategory={onSelectCategory}
            onOpenTask={openTask}
          />
        )}
        {tab === "assessment" && (
          <AssessmentTab research={research} onOpenEvidence={openEvidence} />
        )}
        {tab === "file" && liveAssessment && (
          <FileView
            assessment={liveAssessment}
            // Only a Brief written for this Run; the File tab says when the
            // buyer reads an earlier one.
            brief={
              research.parts.brief?.assessmentId === liveAssessment.id
                ? research.parts.brief
                : null
            }
            onOpenEvidence={openEvidence}
          />
        )}
        {tab === "file" && !liveAssessment && (
          <AssessmentStatus
            assessment={research.parts.assessment}
            onOpenEvidence={openEvidence}
          />
        )}
      </div>
      <CompanyRunSpend research={research} />
      <RunEvidenceSheet
        research={research}
        researchTaskId={researchTaskId}
        openedEvidence={openedEvidence}
        receipts={receipts}
        evidence={evidence}
        error={evidenceError}
        isLoading={isLoadingEvidence}
        hasRequestedModelPayloads={hasModelPayloads}
        onLoadModelPayloads={() => setHasModelPayloads(true)}
        onClose={() => {
          onOpenResearchTask(null)
          setOpenedEvidence(null)
        }}
        onRestoreFocus={() => {
          const trigger = taskFocusId.current
            ? document.querySelector<HTMLButtonElement>(
                `[data-research-task-id="${taskFocusId.current}"]`
              )
            : evidenceFocus.current
          if (trigger?.isConnected) trigger.focus()
        }}
      />
    </div>
  )
}
